import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { ArrowUp, FileText, Download, Users } from 'lucide-react';
import logo from '@/assets/filefinity-logo.png';
import onefinityLogo from '@/assets/onefinity.png';
import { Button } from './ui/button';
import Facebook from './Icons/Facebook';
import Instagram from './Icons/Instagram';
import { useGetStatsQuery } from '@/redux/analytics/analytics.api.slice';
import { selectCurrentUser } from '@/redux/auth/auth.slice';
import { setShowLoginDialog } from '@/redux/global/global.slice';
import FeedbackModal from './FeedbackModal';
import ReviewsModal from './ReviewsModal';
import useHideOnScroll from '@/customHooks/useHideOnScroll';

function Footer() {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const visible = useHideOnScroll();
  const { data: stats } = useGetStatsQuery();
  const dispatch = useDispatch();
  const { isAuthenticated } = useSelector(selectCurrentUser);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [reviewsOpen, setReviewsOpen] = useState(false);

  // Feedback requires a logged-in account; prompt sign-in otherwise.
  const openFeedback = () => {
    if (!isAuthenticated) {
      dispatch(setShowLoginDialog(true));
      return;
    }
    setFeedbackOpen(true);
  };

  return (
    <>
      {/* The bar below is fixed; without this the last content sits under it */}
      <div aria-hidden className="h-[76px] sm:h-[72px]" />
      <footer className="fixed inset-x-0 bottom-0 z-30 px-2 md:px-3">
        <Button
          onClick={scrollToTop}
          className="fixed bottom-[88px] right-3 h-9 w-9 rounded-lg p-2 md:bottom-20 md:right-8 md:h-10 md:w-10"
          size="icon"
          aria-label="Scroll to top"
        >
          <ArrowUp className="h-6 w-6 text-white" />
        </Button>

        <div
          className={`rounded-t-2xl border border-b-0 bg-card bg-gradient-to-tr from-green-500/35 via-emerald-300/10 to-green-600/30 shadow-[0_-8px_30px_rgba(0,0,0,0.10)] transition-transform duration-300 ease-out motion-reduce:transition-none supports-[backdrop-filter]:bg-card/60 supports-[backdrop-filter]:backdrop-blur-xl supports-[backdrop-filter]:backdrop-saturate-150 ${
            visible ? 'translate-y-0' : 'translate-y-full'
          }`}
        >
          <div className="text-gray-600 py-2 px-4 md:px-6">
            {/* Desktop Layout - Single Row */}
            <div className="hidden sm:block">
              <div className="relative flex flex-row justify-between items-center min-h-10">
                <div className="flex items-center gap-3">
                  <Link
                    to="/"
                    title="Filefinity"
                    className="flex items-center space-x-2 hover:text-gray-900"
                  >
                    <img
                      src={logo}
                      alt="logo"
                      className="w-auto h-7 rounded-lg "
                    />
                    <span className="text-sm">Filefinity</span>
                  </Link>
                  <div className="h-6 w-px bg-gray-300 self-center" />
                  <a
                    href="https://www.onefinitycnc.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Onefinity"
                  >
                    <img
                      src={onefinityLogo}
                      alt="onefinity logo"
                      className="h-7 w-auto object-contain"
                    />
                  </a>
                </div>

                {/* Stats Section - Absolutely Centered */}
                {stats && (
                  <div className="absolute left-1/2 -translate-x-1/2 flex items-center gap-6 text-sm">
                    <div className="flex items-center gap-1.5">
                      <FileText className="w-4 h-4 text-primary" />
                      <span className="font-semibold">{stats.totalPosts}</span>
                      <span className="text-gray-500">Posts</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Download className="w-4 h-4 text-primary" />
                      <span className="font-semibold">
                        {stats.totalDownloads}
                      </span>
                      <span className="text-gray-500">Downloads</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Users className="w-4 h-4 text-primary" />
                      <span className="font-semibold">{stats.totalUsers}</span>
                      <span className="text-gray-500">Users</span>
                    </div>
                  </div>
                )}

                <div className="flex items-center gap-2 text-sm">
                  <Button
                    variant="link"
                    onClick={openFeedback}
                    className="text-gray-600 font-normal border-0 p-0 text-sm"
                  >
                    Feedback
                  </Button>
                  <span className="text-gray-300">|</span>
                  <Button
                    variant="link"
                    onClick={() => setReviewsOpen(true)}
                    className="text-gray-600 font-normal border-0 p-0 text-sm"
                  >
                    Reviews
                  </Button>
                  <span className="text-gray-300">|</span>
                  <Link to="/blog" className="hover:text-gray-900">
                    Blog
                  </Link>
                  <span className="text-gray-300">|</span>
                  <Link to="/privacyPolicy" className="hover:text-gray-900">
                    Privacy
                  </Link>
                  <span className="text-gray-300">|</span>
                  <Link to="/legend" className="hover:text-gray-900">
                    Icon Legend
                  </Link>
                  <span className="text-gray-300">|</span>
                  <div className="flex items-center gap-3">
                    <a
                      href="https://facebook.com/FileFinity"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-600 hover:text-gray-900 transition-colors"
                      aria-label="Visit our Facebook page"
                    >
                      <Facebook color="#6B7280" height="20" width="20" />
                    </a>
                    <a
                      href="https://instagram.com/filefinity"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-600 hover:text-gray-900 transition-colors"
                      aria-label="Visit our Instagram profile"
                    >
                      <Instagram color="#6B7280" height="20" width="20" />
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Mobile Layout - Two Rows */}
            <div className="sm:hidden flex flex-col gap-2">
              {/* First Row: Logo and Stats */}
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <Link to="/" title="Filefinity">
                    <img
                      src={logo}
                      alt="logo"
                      className="w-auto h-7 rounded-lg "
                    />
                  </Link>
                  <div className="h-6 w-px bg-gray-300 self-center" />
                  <a
                    href="https://www.onefinitycnc.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Onefinity"
                  >
                    <img
                      src={onefinityLogo}
                      alt="onefinity logo"
                      className="h-7 w-auto object-contain"
                    />
                  </a>
                </div>

                <div className="flex items-center gap-3">
                  {stats && (
                    <div className="flex items-center gap-2.5 text-xs">
                      <div className="flex items-center gap-1">
                        <FileText className="w-3.5 h-3.5 text-primary" />
                        <span className="font-semibold">
                          {stats.totalPosts}
                        </span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Download className="w-3.5 h-3.5 text-primary" />
                        <span className="font-semibold">
                          {stats.totalDownloads}
                        </span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Users className="w-3.5 h-3.5 text-primary" />
                        <span className="font-semibold">
                          {stats.totalUsers}
                        </span>
                      </div>
                    </div>
                  )}
                  <a
                    href="https://facebook.com/FileFinity"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-600 hover:text-gray-900 transition-colors"
                    aria-label="Visit our Facebook page"
                  >
                    <Facebook color="#6B7280" height="18" width="18" />
                  </a>
                  <a
                    href="https://instagram.com/filefinity"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-600 hover:text-gray-900 transition-colors"
                    aria-label="Visit our Instagram profile"
                  >
                    <Instagram color="#6B7280" height="18" width="18" />
                  </a>
                </div>
              </div>

              {/* Second Row: Links */}
              {/* -my cancels the py, so the fixed spacer still matches. */}
              <div className="flex flex-wrap justify-center items-center gap-x-4 gap-y-1 text-xs [&>a]:-my-1.5 [&>a]:py-1.5">
                <Button
                  variant="link"
                  onClick={openFeedback}
                  className="text-gray-600 font-normal border-0 px-0 -my-1.5 py-1.5 text-xs h-auto"
                >
                  Feedback
                </Button>
                <Button
                  variant="link"
                  onClick={() => setReviewsOpen(true)}
                  className="text-gray-600 font-normal border-0 px-0 -my-1.5 py-1.5 text-xs h-auto"
                >
                  Reviews
                </Button>
                <Link to="/blog" className="hover:text-gray-900">
                  Blog
                </Link>
                <Link to="/privacyPolicy" className="hover:text-gray-900">
                  Privacy
                </Link>
                <Link to="/legend" className="hover:text-gray-900">
                  Legend
                </Link>
              </div>
            </div>
          </div>
        </div>

        <FeedbackModal open={feedbackOpen} onOpenChange={setFeedbackOpen} />
        <ReviewsModal open={reviewsOpen} onOpenChange={setReviewsOpen} />
      </footer>
    </>
  );
}

export default Footer;
